import { Injectable } from '@angular/core';
import { PatientEstimateEventArgs } from '@rivethealth/data/analytics/patient-analytics-events';
import {
  EstimatePaymentRequest,
  EstimatePaymentRequestOption,
} from '@rivethealth/data/estimation';
import { combineLatest, merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { PatientEstimationService } from '../../api/patient-estimation.service';
import { ViewEventArgumentService } from '../../common/analytics/common-analytics.service';
import { loadMap, success } from '../../common/helpers/loadable';
import { NumberParam, StringParam } from '../../common/state/route';
import { RouteService } from '../../common/state/route.service';

export type CalculatedOption = EstimatePaymentRequestOption & {
  amount: number;
};

function calculate(
  total: number,
  paymentRequest: EstimatePaymentRequest,
): CalculatedOption[] {
  return paymentRequest.options
    .map(option =>
      option.type === 'percent'
        ? { ...option, amount: option.value * total }
        : { ...option, amount: option.value },
    )
    .sort((a, b) => (a.amount < b.amount ? -1 : 1));
}

@Injectable()
export class PatientEstimationClientService
  implements ViewEventArgumentService<PatientEstimateEventArgs>
{
  readonly jwt = this.routeService.getQuery(new StringParam('jwt'));
  readonly communicationType = this.routeService.getQuery<'email' | 'sms'>(
    new StringParam('type'),
  );

  readonly providerLogoUrl = this.jwt.pipe(
    map(jwt => this.patientEstimationService.getPatientEstimationLogo(jwt!)),
  );

  readonly estimationLoadable = this.jwt.pipe(
    loadMap(jwt => this.patientEstimationService.get(jwt!)),
    map(loadable => {
      if (loadable.erred && loadable.error) {
        return { loading: false, erred: false, data: null };
      }
      return loadable;
    }),
  );

  readonly pdfUrl = this.jwt.pipe(
    map(jwt => this.patientEstimationService.pdfUrl(jwt!)),
  );

  readonly creditCardsLoadable = this.jwt.pipe(
    loadMap(jwt => this.patientEstimationService.getPatientCreditCards(jwt!)),
  );

  readonly estimation = this.estimationLoadable.pipe(success());

  readonly accountIdLoadable = this.jwt.pipe(
    loadMap(jwt => this.patientEstimationService.getPatientAccountId(jwt!)),
  );

  readonly accountId = this.accountIdLoadable.pipe(success());

  readonly args: Observable<PatientEstimateEventArgs> = this.estimation.pipe(
    map(estimation => ({
      // We removed 'estimate id'. Feel free to expand this. Properties here
      // will be applied to all patient estimate flow events.
    })),
  );

  readonly total = this.estimation.pipe(map(({ total }) => total));

  readonly payment = this.estimation.pipe(map(({ payment }) => payment));

  readonly paymentToDate = this.payment.pipe(
    map(payment => (payment && payment.totalAmount ? payment.totalAmount : 0)),
  );

  readonly remainingDue = combineLatest([this.total, this.paymentToDate]).pipe(
    map(([total, payment]) => total - payment),
  );

  readonly lastPaymentDate = this.payment.pipe(
    map(payment => payment?.dateReceived),
  );

  readonly paymentRequest = this.estimation.pipe(
    map(({ paymentRequest }) => paymentRequest),
    filter(paymentRequest => !!paymentRequest),
  );

  readonly paymentDueDate = this.paymentRequest.pipe(
    map(o => o!),
    map(({ dueDate }) => dueDate),
  );

  readonly customAmount = this.routeService.query(
    new NumberParam('customAmount'),
  );

  readonly calculatedPaymentOptions = combineLatest([
    this.total,
    this.paymentRequest,
    this.remainingDue,
    this.customAmount,
  ]).pipe(
    map(([total, paymentRequest, remainingDue, customAmount]) =>
      total == remainingDue
        ? calculate(total, paymentRequest!)
        : [
            {
              type: 'flat',
              value: remainingDue,
              amount: remainingDue,
            },
            {
              type: 'flat',
              value: customAmount ? customAmount : 0,
              amount: customAmount ? customAmount : 0,
            },
          ],
    ),
  );

  readonly minimumPaymentOption = this.calculatedPaymentOptions.pipe(
    map(options => options[0]),
  );

  readonly selectedOptionIndex = this.routeService.query(
    new NumberParam('optionId'),
  );

  readonly selectedOption = combineLatest([
    this.calculatedPaymentOptions,
    this.selectedOptionIndex,
  ]).pipe(map(([options, idx]) => (idx ? options[idx] : options[0])));

  readonly savedEmailLoadable = this.jwt.pipe(
    loadMap(jwt => this.patientEstimationService.patientEmail(jwt!)),
  );

  private readonly savedEmail = this.savedEmailLoadable.pipe(
    success(),
    map(({ email }) => email!),
    distinctUntilChanged(),
  );

  readonly userEmail = new Subject<string>();

  readonly receiptEmail = merge(this.savedEmail, this.userEmail).pipe(
    shareReplay(1),
  );

  constructor(
    private readonly routeService: RouteService,
    private readonly patientEstimationService: PatientEstimationService,
  ) {}
}
